import React, { useState, useMemo, useRef, useCallback, memo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  Card,
  CardContent,
  Tabs,
  Tab,
  CircularProgress
} from '@mui/material';
import {
  Add,
  Person,
  Business,
  Check,
  Close
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import axios from 'axios';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import AdminPayoutMatrix from './AdminPayoutMatrix';

interface PayoutRule {
  id: number;
  gameName: string;
  operatorId: number;
  operatorName: string;
  operatorCode: string;
  betType: string;
  prizeTier: string;
  multiplier: number;
  effectiveFrom: string;
  effectiveTo?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UserPayoutRule {
  id: number;
  userId: number;
  userEmail: string;
  userFullName: string;
  gameId: number;
  operatorId: number;
  operatorName: string;
  betType: string;
  prizeTier: string;
  multiplier: number;
  effectiveFrom: string;
  effectiveTo?: string;
  createdAt: string;
  createdByEmail?: string;
}

interface CreateUserPayoutRuleDto {
  userId: number;
  gameId: number;
  betType: string;
  prizeTier: string;
  multiplier: number;
  effectiveFrom: string;
  effectiveTo?: string;
}

interface UserSummary {
  userId: number;
  userFullName: string;
  userEmail: string;
  operatorName: string;
  rulesCount: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`payout-tabpanel-${index}`}
      aria-labelledby={`payout-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Memoized table cell component to prevent unnecessary re-renders
const PayoutTableCell = memo(({ 
  rule, 
  betType, 
  prizeTier, 
  operatorKey, 
  isAdmin, 
  editingStates, 
  setEditingStates, 
  handleEditAmount, 
  handleSaveAmount, 
  handleCancelEdit 
}: {
  rule: any;
  betType: string;
  prizeTier: string;
  operatorKey: string;
  isAdmin: boolean;
  editingStates: any;
  setEditingStates: any;
  handleEditAmount: any;
  handleSaveAmount: any;
  handleCancelEdit: any;
}) => {
  if (!rule) {
    return (
      <TableCell align="center" sx={{ border: '1px solid', borderColor: 'divider', p: 0.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          -
        </Typography>
      </TableCell>
    );
  }

  const isEditing = isAdmin && editingStates[operatorKey]?.ruleId === rule.id;
  
  return (
    <TableCell align="center" sx={{ border: '1px solid', borderColor: 'divider', p: 0.5 }}>
      {isEditing ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <TextField
            size="small"
            type="text"
            value={editingStates[operatorKey]?.amounts.get(rule.id) || ''}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                setEditingStates((prev: any) => {
                  const newStates = { ...prev };
                  if (!newStates[operatorKey]) {
                    newStates[operatorKey] = { ruleId: null, amounts: new Map() };
                  }
                  const newAmounts = new Map(newStates[operatorKey].amounts);
                  newAmounts.set(rule.id, value);
                  newStates[operatorKey] = {
                    ...newStates[operatorKey],
                    amounts: newAmounts
                  };
                  return newStates;
                });
              }
            }}
            sx={{ width: 70, '& .MuiInputBase-input': { textAlign: 'center', fontSize: '0.75rem' } }}
          />
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" color="primary" onClick={() => handleSaveAmount(rule.id)} sx={{ p: 0.25 }}>
              <Check fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => handleCancelEdit(operatorKey)} sx={{ p: 0.25 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            cursor: isAdmin ? 'pointer' : 'default',
            p: 0.5,
            borderRadius: 0.5,
            '&:hover': isAdmin ? { 
              bgcolor: rule.isDefault ? 'grey.100' : 'success.50' 
            } : {},
            textAlign: 'center',
            minHeight: 50,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            bgcolor: rule.isDefault ? 'grey.50' : 'success.50',
            border: rule.isDefault ? '1px solid' : '2px solid',
            borderColor: rule.isDefault ? 'grey.200' : 'success.light',
            position: 'relative'
          }}
          onClick={() => isAdmin && handleEditAmount(rule.id, rule.multiplier, operatorKey)}
        >
          {rule.isDefault && (
            <Typography 
              variant="caption" 
              color="grey.500" 
              sx={{ 
                fontSize: '0.5rem', 
                position: 'absolute', 
                top: 2, 
                left: 2,
                fontWeight: 'bold'
              }}
            >
              DEFAULT
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: rule.isDefault ? 1 : 0 }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              {rule.isDefault ? '🔧' : '👤'}
            </Typography>
            <Typography 
              variant="body2" 
              fontWeight="bold" 
              color={rule.isDefault ? 'grey.600' : 'success.dark'}
            >
              {rule.multiplier.toFixed(2)}
            </Typography>
          </Box>
          {isAdmin && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', mt: 0.5 }}>
              ✏️
            </Typography>
          )}
        </Box>
      )}
    </TableCell>
  );
});

const PayoutRulesManagement: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();
  

  const [tabValue, setTabValue] = useState(0);
  const [createDialog, setCreateDialog] = useState(false);
  const [bulkCreateDialog, setBulkCreateDialog] = useState(false);
  
  // New state for user selection view
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce search term to reduce re-renders
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(userSearchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [userSearchTerm]);
  const [editingStates, setEditingStates] = useState<{[operatorKey: string]: {ruleId: number | null, amounts: Map<number, string>}}>({});

  
  // Form state for creating/editing user payout rules
  const [formData, setFormData] = useState<CreateUserPayoutRuleDto>({
    userId: 0,
    gameId: 0,
    betType: '',
    prizeTier: '',
    multiplier: 0,
    effectiveFrom: new Date().toISOString().split('T')[0],
    effectiveTo: undefined
  });

  // String values for input fields to avoid parsing on every keystroke
  const [multiplierInput, setMultiplierInput] = useState('');
  const [bulkMultiplierInput, setBulkMultiplierInput] = useState('');

  // Form state for bulk rule creation
  const [bulkFormData, setBulkFormData] = useState({
    operatorId: 0,
    betType: '',
    prizeTier: '',
    multiplier: 0,
    effectiveFrom: new Date().toISOString().split('T')[0],
    effectiveTo: undefined as string | undefined
  });

  // Fetch default payout rules
  const { data: payoutRules, isLoading: payoutRulesLoading } = useQuery<PayoutRule[]>({
    queryKey: ['payout-rules'],
    queryFn: async () => {
      const response = await axios.get('/admin/payout-rules');
      return response.data;
    },
  });

  // Fetch all users
  const { data: allUsersData, isLoading: allUsersLoading } = useQuery<{users: any[]}>({
    queryKey: ['all-users'],
    queryFn: async () => {
      const response = await axios.get('/admin/users?page=1&pageSize=1000');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000  // 10 minutes
  });

  // Fetch user-specific payout rules for selected user only
  const { data: selectedUserPayoutRules, isLoading: selectedUserPayoutRulesLoading, refetch: refetchSelectedUserRules } = useQuery<UserPayoutRule[]>({
    queryKey: ['user-payout-rules', selectedUserId],
    queryFn: async () => {
      if (!selectedUserId) return [];
      const response = await axios.get(`/admin/users/${selectedUserId}/payout-rules`);
      return response.data || [];
    },
    enabled: !!selectedUserId, // Only fetch when a user is selected
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000  // 5 minutes
  });

  // Create user payout rule mutation
  const createUserPayoutRuleMutation = useMutation({
    mutationFn: async (data: CreateUserPayoutRuleDto) => {
      const response = await axios.post('/admin/user-payout-rules', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-payout-rules', selectedUserId] });
      refetchSelectedUserRules();
      setCreateDialog(false);
      resetForm();
    }
  });



  // Mutation for bulk rule creation
  const bulkCreateRulesMutation = useMutation({
    mutationFn: async (data: typeof bulkFormData) => {
      const response = await axios.post('/admin/bulk-create-user-payout-rules', data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user-payout-rules'] });
      queryClient.invalidateQueries({ queryKey: ['all-users'] });
      setBulkCreateDialog(false);
      resetBulkForm();
      alert(`Successfully created ${data.totalRulesCreated} payout rules for ${data.usersProcessed} users!`);
    },
    onError: (error) => {
      console.error('Error creating bulk rules:', error);
      alert('Failed to create bulk payout rules. Please try again.');
    },
  });



  const resetForm = () => {
    setFormData({
      userId: 0,
      gameId: 0,
      betType: '',
      prizeTier: '',
      multiplier: 0,
      effectiveFrom: new Date().toISOString().split('T')[0],
      effectiveTo: undefined
    });
    setMultiplierInput('');
  };

  const resetBulkForm = () => {
    setBulkFormData({
      operatorId: 0,
      betType: '',
      prizeTier: '',
      multiplier: 0,
      effectiveFrom: new Date().toISOString().split('T')[0],
      effectiveTo: undefined
    });
    setBulkMultiplierInput('');
  };

  // User selection and editing functions
  const handleUserSelect = useCallback((user: UserSummary) => {
    setSelectedUserId(user.userId);
    setSelectedUserName(user.userFullName);
    // Clear all operator-specific editing states when switching users
    setEditingStates({});
    // Reset the default rule ID counter to prevent overlapping IDs
    defaultRuleIdCounterRef.current = -1;
    // Trigger useMemo recalculation to ensure fresh rule IDs
    setUserSwitchTrigger(prev => prev + 1);
  }, []);

  const handleEditAmount = useCallback((ruleId: number, currentAmount: number, operatorKey: string) => {
    setEditingStates(prev => ({
      ...prev,
      [operatorKey]: {
        ruleId: ruleId,
        amounts: new Map(prev[operatorKey]?.amounts || new Map()).set(ruleId, currentAmount.toString())
      }
    }));
  }, []);

  const getCurrentEditingRule = (operatorKey: string) => {
    const editingState = editingStates[operatorKey];
    if (!editingState || editingState.ruleId === null) return null;
    
    const matrix = operatorRuleMatrices[operatorKey]?.matrix;
    if (!matrix) return null;
    
    for (const betType in matrix) {
      for (const prizeTier in matrix[betType]) {
        const rule = matrix[betType][prizeTier];
        if (rule.id === editingState.ruleId) {
          return rule;
        }
      }
    }
    return null;
  };

  const handleSaveAmount = async (ruleId: number) => {
    try {
      if (!selectedUserId) {
        console.error('No user selected');
        return;
      }

      // Find which operator this rule belongs to
      let operatorKey = '';
      let currentRule: UserPayoutRule | null = null;
      
      for (const opKey in editingStates) {
        if (editingStates[opKey].ruleId === ruleId) {
          operatorKey = opKey;
          currentRule = getCurrentEditingRule(opKey);
          break;
        }
      }
      
      if (!currentRule || !operatorKey) {
        console.error('No rule found for editing');
        return;
      }

      const editingAmount = editingStates[operatorKey]?.amounts.get(ruleId) || '0';

      if (ruleId < 0) {
        // This is a default rule (negative ID), create a new user-specific rule
        
        await axios.post('/admin/user-payout-rules', {
          UserId: selectedUserId,
          GameId: currentRule.gameId,
          BetType: currentRule.betType,
          PrizeTier: currentRule.prizeTier,
          Multiplier: parseFloat(editingAmount) || 0,
          EffectiveFrom: new Date().toISOString().split('T')[0]
        });
      } else {
        // This is an existing user-specific rule, update it
        // Verify the rule belongs to the selected user
        const ruleToUpdate = selectedUserPayoutRules?.find(rule => rule.id === ruleId);
        if (!ruleToUpdate || ruleToUpdate.userId !== selectedUserId) {
          console.error('Rule does not belong to selected user');
          return;
        }

        await axios.put(`/admin/user-payout-rules/${ruleId}`, {
          Multiplier: parseFloat(editingAmount) || 0,
          EffectiveFrom: new Date().toISOString().split('T')[0],
          EffectiveTo: ruleToUpdate.effectiveTo
        });
      }
      queryClient.invalidateQueries({ queryKey: ['user-payout-rules'] });
      refetchSelectedUserRules();
      
      // Clear editing state for this operator
      setEditingStates(prev => {
        const newStates = { ...prev };
        if (newStates[operatorKey]) {
          newStates[operatorKey] = {
            ruleId: null,
            amounts: new Map(newStates[operatorKey].amounts)
          };
          newStates[operatorKey].amounts.delete(ruleId);
        }
        return newStates;
      });
    } catch (error: any) {
      console.error('Error updating payout amount:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        console.error('Response headers:', error.response.headers);
      } else if (error.request) {
        console.error('Request made but no response:', error.request);
      } else {
        console.error('Error message:', error.message);
      }
      alert(`Save failed: ${error.response?.data?.message || error.message || 'Unknown error'}`);
    }
  };

  const handleCancelEdit = (operatorKey: string) => {
    setEditingStates(prev => {
      const newStates = { ...prev };
      if (newStates[operatorKey]) {
        const ruleId = newStates[operatorKey].ruleId;
        newStates[operatorKey] = {
          ruleId: null,
          amounts: new Map(newStates[operatorKey].amounts)
        };
        if (ruleId !== null) {
          newStates[operatorKey].amounts.delete(ruleId);
        }
      }
      return newStates;
    });
  };

  // Get unique bet types and prize tiers for grid from default payout rules
  const betTypes = useMemo((): string[] => {
    return ['Big', 'Small', '4A', 'A', 'ABC', '5D', '6D'];
  }, []);

  const prizeTiers = useMemo((): string[] => {
    return ['Prize 1', 'Prize 2', 'Prize 3', 'Starters/Prize 4', 'Consolation/Prize 5', 'Prize 6'];
  }, []);

  // Create user summaries from all users, not just those with payout rules
  const userSummaries = useMemo(() => {
    const usersData = allUsersData as {users: any[]} | undefined;

    
    if (!usersData?.users) {
      return [];
    }
    
    return usersData.users.map((user: any) => {
      return {
        userId: user.id,
        userFullName: user.fullName || user.FullName || `${user.firstName || user.FirstName} ${user.lastName || user.LastName}`,
        userEmail: user.email || user.Email,
        operatorName: 'Default Operator', // You may need to fetch this from user data
        rulesCount: 0 // Will be updated when user is selected
      };
    }).sort((a: any, b: any) => a.userFullName.localeCompare(b.userFullName));
  }, [allUsersData]);

  // Filter users based on search term
  const filteredUserSummaries = useMemo(() => {
    if (!debouncedSearchTerm) return userSummaries;
    
    const searchLower = debouncedSearchTerm.toLowerCase();
    return userSummaries.filter((user: UserSummary) => 
      user.userFullName.toLowerCase().includes(searchLower) ||
      user.userEmail.toLowerCase().includes(searchLower) ||
      user.operatorName.toLowerCase().includes(searchLower)
    );
  }, [userSummaries, debouncedSearchTerm]);

  // Counter for generating unique negative IDs for default rules (outside useMemo to persist across user selections)
  const defaultRuleIdCounterRef = useRef(-1);
  // State to force useMemo recalculation when user switches
  const [userSwitchTrigger, setUserSwitchTrigger] = useState(0);

  // Create operator-grouped rule matrices for selected user with default fallback
  const operatorRuleMatrices = useMemo(() => {
    console.time('operatorRuleMatrices calculation');
    console.log('useMemo triggered with:', {
      selectedUserId,
      selectedUserPayoutRulesLength: selectedUserPayoutRules?.length,
      payoutRulesLength: payoutRules?.length,
      userSwitchTrigger
    });
    
    if (!selectedUserId) {
      console.timeEnd('operatorRuleMatrices calculation');
      return {};
    }
    
    const operatorMatrices: Record<string, {
      operatorId: number;
      operatorName: string;
      matrix: Record<string, Record<string, UserPayoutRule>>;
    }> = {};
    
    // First, populate with user-specific rules if available
    if (selectedUserPayoutRules && Array.isArray(selectedUserPayoutRules) && selectedUserPayoutRules.length > 0) {
      selectedUserPayoutRules.forEach((rule: UserPayoutRule) => {
        const operatorKey = `${rule.operatorId}-${rule.operatorName}`;
        
        if (!operatorMatrices[operatorKey]) {
          operatorMatrices[operatorKey] = {
            operatorId: rule.operatorId,
            operatorName: rule.operatorName,
            matrix: {}
          };
        }
        
        if (!operatorMatrices[operatorKey].matrix[rule.betType]) {
          operatorMatrices[operatorKey].matrix[rule.betType] = {};
        }
        
        // Check if this is a backend-provided default rule
        const isBackendDefault = rule.createdByEmail === 'System (Default Rules)';
        
        operatorMatrices[operatorKey].matrix[rule.betType][rule.prizeTier] = {
          ...rule,
          id: isBackendDefault ? defaultRuleIdCounterRef.current-- : rule.id, // Use negative ID for backend defaults
          isDefault: isBackendDefault
        } as UserPayoutRule & { isDefault?: boolean };
      });
    }
    
    // Then, fill missing combinations with default rules
    if (payoutRules && Array.isArray(payoutRules)) {
      payoutRules.forEach((defaultRule: PayoutRule) => {
        const operatorKey = `${defaultRule.operatorId}-${defaultRule.operatorName}`;
        
        if (!operatorMatrices[operatorKey]) {
          operatorMatrices[operatorKey] = {
            operatorId: defaultRule.operatorId,
            operatorName: defaultRule.operatorName,
            matrix: {}
          };
        }
        
        if (!operatorMatrices[operatorKey].matrix[defaultRule.betType]) {
          operatorMatrices[operatorKey].matrix[defaultRule.betType] = {};
        }
        
        const existingRule = operatorMatrices[operatorKey].matrix[defaultRule.betType]?.[defaultRule.prizeTier];
        
        if (!existingRule) {
          // No rule exists (neither user-specific nor backend default), add frontend default rule
          operatorMatrices[operatorKey].matrix[defaultRule.betType][defaultRule.prizeTier] = {
            id: defaultRuleIdCounterRef.current--, // Generate unique negative ID for each default rule
            userId: selectedUserId,
            userEmail: selectedUserName.split(' ').pop() || '', // Fallback email
            userFullName: selectedUserName,
            gameId: defaultRule.operatorId, // Using operatorId as gameId since PayoutRule doesn't have gameId
            operatorId: defaultRule.operatorId,
            operatorName: defaultRule.operatorName,
            betType: defaultRule.betType,
            prizeTier: defaultRule.prizeTier,
            multiplier: defaultRule.multiplier,
            effectiveFrom: defaultRule.effectiveFrom,
            effectiveTo: defaultRule.effectiveTo,
            createdAt: new Date().toISOString(),
            createdBy: 0,
            isDefault: true // Flag to indicate this is a default rule
          } as UserPayoutRule & { isDefault?: boolean };
        }
        // If existingRule exists and is not already marked as default, it's a user-specific rule
        // Don't change its isDefault flag - user-specific rules should always show as user-specific
      });
    }
    
    console.timeEnd('operatorRuleMatrices calculation');
    console.log('operatorRuleMatrices result:', Object.keys(operatorMatrices).length, 'operators');
    return operatorMatrices;
  }, [selectedUserPayoutRules, selectedUserId, selectedUserName, payoutRules, userSwitchTrigger]);

  const handleCreateRule = () => {
    const userIdElement = document.querySelector('[data-testid="create-user-id-input"] input') as HTMLInputElement;
    const gameIdElement = document.querySelector('[data-testid="create-game-id-input"] input') as HTMLInputElement;
    const betTypeElement = document.querySelector('[data-testid="create-bet-type-select"]') as HTMLSelectElement;
    const prizeTierElement = document.querySelector('[data-testid="create-prize-tier-select"]') as HTMLSelectElement;
    const multiplierElement = document.querySelector('[data-testid="create-multiplier-input"] input') as HTMLInputElement;
    const effectiveFromElement = document.querySelector('[data-testid="create-effective-from-input"] input') as HTMLInputElement;
    const effectiveToElement = document.querySelector('[data-testid="create-effective-to-input"] input') as HTMLInputElement;
    
    const submitData = {
      userId: parseInt(userIdElement?.value || '0') || 0,
      gameId: parseInt(gameIdElement?.value || '0') || 0,
      betType: betTypeElement?.value || '',
      prizeTier: prizeTierElement?.value || '',
      multiplier: parseFloat(multiplierElement?.value || '0') || 0,
      effectiveFrom: effectiveFromElement?.value || new Date().toISOString().split('T')[0],
      effectiveTo: effectiveToElement?.value || undefined
    };
    createUserPayoutRuleMutation.mutate(submitData);
  };

  const handleBulkCreateRules = () => {
    const operatorIdElement = document.querySelector('[data-testid="bulk-operator-id-input"] input') as HTMLInputElement;
    const betTypeElement = document.querySelector('[data-testid="bulk-bet-type-select"]') as HTMLSelectElement;
    const prizeTierElement = document.querySelector('[data-testid="bulk-prize-tier-select"]') as HTMLSelectElement;
    const multiplierElement = document.querySelector('[data-testid="bulk-multiplier-input"] input') as HTMLInputElement;
    const effectiveFromElement = document.querySelector('[data-testid="bulk-effective-from-input"] input') as HTMLInputElement;
    const effectiveToElement = document.querySelector('[data-testid="bulk-effective-to-input"] input') as HTMLInputElement;
    
    const submitData = {
      operatorId: parseInt(operatorIdElement?.value || '0') || 0,
      betType: betTypeElement?.value || '',
      prizeTier: prizeTierElement?.value || '',
      multiplier: parseFloat(multiplierElement?.value || '0') || 0,
      effectiveFrom: effectiveFromElement?.value || new Date().toISOString().split('T')[0],
      effectiveTo: effectiveToElement?.value || undefined
    };
    bulkCreateRulesMutation.mutate(submitData);
  };



  const formatDate = (dateString: string) => {
    if (!dateString) return 'Invalid Date';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return format(date, 'MMM dd, yyyy');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h5" gutterBottom>
          Payout Rules Management
        </Typography>
        
        <Alert 
          severity={isAdmin ? "info" : "warning"} 
          sx={{ mb: 3 }}
        >
          {isAdmin 
            ? "You have administrator privileges and can create, edit, and delete payout rules for all users."
            : "You are viewing payout rules in read-only mode. Only administrators can modify payout rules."
          }
        </Alert>
        
        {/* Authentication Status */}
        {!user && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body1" gutterBottom>
              <strong>Authentication Required</strong>
            </Typography>
            <Typography variant="body2">
              You must be logged in as an administrator to view payout rules. Please log in with:
              <br />Email: <strong>admin@lotterysystem.com</strong>
              <br />Password: <strong>Admin123!</strong>
            </Typography>
          </Alert>
        )}
        
        {user && !isAdmin && (
           <Alert severity="warning" sx={{ mb: 2 }}>
             <Typography variant="body1" gutterBottom>
               <strong>Admin Access Required</strong>
             </Typography>
             <Typography variant="body2">
               You are logged in as {user.email}, but administrator privileges are required to manage payout rules.
             </Typography>
           </Alert>
         )}
         
         {/* Authentication and Data Status Info */}
       <Alert severity={user?.role === 'admin' ? 'success' : 'warning'} sx={{ mb: 2 }}>
         <Typography variant="body2">
           Status: {user?.role === 'admin' ? '✅ Admin Access' : '⚠️ Login Required'} | 
           User: {user?.email || 'Not logged in'} | 
           Users: {allUsersLoading ? 'Loading...' : `${allUsersData?.users?.length || 0} found`} | 
           Rules: {selectedUserPayoutRulesLoading ? 'Loading...' : `${selectedUserPayoutRules?.length || 0} found`}
         </Typography>
         {user?.role !== 'admin' && (
           <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
             Please log in as admin (admin@lotterysystem.com / Admin123!) at <a href="/login" target="_blank">/login</a> to access payout rules data.
           </Typography>
         )}
       </Alert>
        
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => setTabValue(newValue)}
            variant="fullWidth"
          >
            <Tab icon={<Business />} label="Default Payout Rules" />
            <Tab icon={<Person />} label="User-Specific Rules" />
          </Tabs>
        </Paper>

        {/* Default Payout Rules Tab */}
        <TabPanel value={tabValue} index={0}>
          <AdminPayoutMatrix />
        </TabPanel>

        {/* User-Specific Payout Rules Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              User-Specific Payout Rules
            </Typography>
            {isAdmin && (
              <Box sx={{ display: 'flex', gap: 2 }}>

                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    setBulkMultiplierInput(bulkFormData.multiplier.toString());
                    setBulkCreateDialog(true);
                  }}
                  disabled={bulkCreateRulesMutation.isPending}
                >
                  {bulkCreateRulesMutation.isPending ? 'Creating...' : 'Bulk Create Rules'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => {
                    setMultiplierInput(formData.multiplier.toString());
                    setCreateDialog(true);
                  }}
                >
                  Create Rule
                </Button>
              </Box>
            )}
          </Box>

          {/* Two Panel Layout */}
          <Box sx={{ display: 'flex', gap: 3, height: '70vh' }}>
            {/* Left Panel - User List */}
            <Paper sx={{ width: '30%', p: 2, overflow: 'auto' }}>
              <Typography variant="h6" gutterBottom>
                Users
              </Typography>
              
              {/* User Search */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  label="Search Users"
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Search by name, email..."
                />
              </Box>
              
              {allUsersLoading ? (
                <Typography>Loading users...</Typography>
              ) : (
                <Box>
                  {filteredUserSummaries.length === 0 ? (
                    <Typography color="text.secondary" align="center">
                      No users found
                    </Typography>
                  ) : (
                    filteredUserSummaries.map((user: UserSummary) => (
                      <Card 
                        key={user.userId} 
                        sx={{ 
                          mb: 2, 
                          cursor: 'pointer',
                          border: selectedUserId === user.userId ? '2px solid' : '1px solid',
                          borderColor: selectedUserId === user.userId ? 'primary.main' : 'divider',
                          bgcolor: selectedUserId === user.userId ? 'primary.50' : 'background.paper',
                          '&:hover': {
                            bgcolor: selectedUserId === user.userId ? 'primary.100' : 'action.hover'
                          }
                        }}
                        onClick={() => handleUserSelect(user)}
                      >
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {user.userFullName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {user.userEmail}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Chip label={user.operatorName} size="small" variant="outlined" />
                            <Typography variant="caption" color="text.secondary">
                              {user.rulesCount} rules
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </Box>
              )}
            </Paper>
            
            {/* Right Panel - Payout Grid */}
            <Paper sx={{ width: '70%', p: 2, overflow: 'auto' }}>
              {selectedUserId === null ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                  <Person sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6" gutterBottom>
                    Select a User
                  </Typography>
                  <Typography variant="body2" align="center">
                    Choose a user from the left panel to view and manage their payout rules
                  </Typography>
                </Box>
              ) : selectedUserPayoutRulesLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                  <CircularProgress sx={{ mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Loading Payout Rules
                  </Typography>
                  <Typography variant="body2" align="center">
                    Fetching payout rules for {selectedUserName}...
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6">
                      Payout Rules for {selectedUserName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {isAdmin ? 'Click on amounts to edit them' : 'View-only mode'} • Grouped by Operator
                    </Typography>
                    
                    {/* Legend */}
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" fontWeight="bold" color="text.secondary">
                        Legend:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                         <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>👤</Typography>
                         <Box sx={{ width: 12, height: 12, bgcolor: 'success.light', borderRadius: 0.5 }} />
                         <Typography variant="caption" color="success.dark" fontWeight="bold">
                           User-Specific Rules
                         </Typography>
                       </Box>
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                         <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>🔧</Typography>
                         <Box sx={{ width: 12, height: 12, bgcolor: 'grey.300', borderRadius: 0.5 }} />
                         <Typography variant="caption" color="grey.600" fontWeight="bold">
                           Default Rules
                         </Typography>
                       </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          No Rule Available
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  {/* Operator-Grouped Payout Grids */}
                  {Object.entries(operatorRuleMatrices).map(([operatorKey, operatorData]) => (
                    <Box key={operatorKey} sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                        {operatorData.operatorName}
                      </Typography>
                      <TableContainer sx={{ border: '1px solid', borderColor: 'divider' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'primary.main' }}>
                              <TableCell sx={{ color: 'white', fontWeight: 'bold', border: '1px solid white' }}>Details</TableCell>
                              {betTypes.map(betType => (
                                <TableCell key={betType} align="center" sx={{ color: 'white', fontWeight: 'bold', border: '1px solid white', minWidth: 80 }}>
                                  {betType}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {prizeTiers.map(prizeTier => (
                              <TableRow key={prizeTier}>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100', border: '1px solid', borderColor: 'divider' }}>
                                  {prizeTier}
                                </TableCell>
                                {betTypes.map(betType => {
                                  const rule = operatorData.matrix[betType]?.[prizeTier];
                                  return (
                                    <PayoutTableCell
                                      key={`${betType}-${prizeTier}`}
                                      rule={rule}
                                      betType={betType}
                                      prizeTier={prizeTier}
                                      operatorKey={operatorKey}
                                      isAdmin={isAdmin}
                                      editingStates={editingStates}
                                      setEditingStates={setEditingStates}
                                      handleEditAmount={handleEditAmount}
                                      handleSaveAmount={handleSaveAmount}
                                      handleCancelEdit={handleCancelEdit}
                                    />
                                  );
                                })}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Box>
        </TabPanel>



        {/* Create User Payout Rule Dialog */}
        <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Create User-Specific Payout Rule</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Alert severity="info">
                Create a custom payout multiplier for a specific user and operator combination.
              </Alert>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
                <TextField
                  label="User ID"
                  type="number"
                  defaultValue={formData.userId}
                  fullWidth
                  required
                  data-testid="create-user-id-input"
                />
                <TextField
                  label="Game ID"
                  type="number"
                  defaultValue={formData.gameId}
                  fullWidth
                  required
                  data-testid="create-game-id-input"
                />

                <FormControl fullWidth required>
                   <InputLabel>Bet Type</InputLabel>
                   <Select
                     defaultValue={formData.betType}
                     label="Bet Type"
                     data-testid="create-bet-type-select"
                   >
                     <MenuItem value="Big">Big</MenuItem>
                     <MenuItem value="Small">Small</MenuItem>
                     <MenuItem value="4A">4A</MenuItem>
                     <MenuItem value="A">A</MenuItem>
                     <MenuItem value="ABC">ABC</MenuItem>
                     <MenuItem value="5D">5D</MenuItem>
                     <MenuItem value="6D">6D</MenuItem>
                     <MenuItem value="2A">2A</MenuItem>
                   </Select>
                 </FormControl>
                 <FormControl fullWidth required>
                   <InputLabel>Prize Tier</InputLabel>
                   <Select
                     defaultValue={formData.prizeTier}
                     label="Prize Tier"
                     data-testid="create-prize-tier-select"
                   >
                     <MenuItem value="Prize 1">Prize 1</MenuItem>
                     <MenuItem value="Prize 2">Prize 2</MenuItem>
                     <MenuItem value="Prize 3">Prize 3</MenuItem>
                     <MenuItem value="Starters/Prize 4">Starters/Prize 4</MenuItem>
                     <MenuItem value="Consolation/Prize 5">Consolation/Prize 5</MenuItem>
                     <MenuItem value="Prize 6">Prize 6</MenuItem>
                   </Select>
                 </FormControl>
                <TextField
                  label="Multiplier"
                  type="number"
                  defaultValue={multiplierInput}
                  fullWidth
                  required
                  helperText="e.g., 2.5 for 2.5x multiplier"
                  data-testid="create-multiplier-input"
                />
                <TextField
                  label="Effective From"
                  type="date"
                  defaultValue={formData.effectiveFrom}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  data-testid="create-effective-from-input"
                />
                <TextField
                  label="Effective To (Optional)"
                  type="date"
                  defaultValue={formData.effectiveTo || ''}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText="Leave empty for ongoing rule"
                  data-testid="create-effective-to-input"
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateRule}
              variant="contained"
              disabled={createUserPayoutRuleMutation.isPending}
            >
              {createUserPayoutRuleMutation.isPending ? 'Creating...' : 'Create Rule'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Create User Payout Rules Dialog */}
        <Dialog open={bulkCreateDialog} onClose={() => setBulkCreateDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Bulk Create Payout Rules for All Users</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Alert severity="warning">
                This will create the specified payout rule for ALL users who don't already have a rule for this bet type and prize tier combination.
              </Alert>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
                <TextField
                  label="Operator ID"
                  type="number"
                  defaultValue={bulkFormData.operatorId}
                  fullWidth
                  required
                  data-testid="bulk-operator-id-input"
                />

                <FormControl fullWidth required>
                   <InputLabel>Bet Type</InputLabel>
                   <Select
                     defaultValue={bulkFormData.betType}
                     label="Bet Type"
                     data-testid="bulk-bet-type-select"
                   >
                     <MenuItem value="Big">Big</MenuItem>
                     <MenuItem value="Small">Small</MenuItem>
                     <MenuItem value="4A">4A</MenuItem>
                     <MenuItem value="A">A</MenuItem>
                     <MenuItem value="ABC">ABC</MenuItem>
                     <MenuItem value="5D">5D</MenuItem>
                     <MenuItem value="6D">6D</MenuItem>
                     <MenuItem value="2A">2A</MenuItem>
                   </Select>
                 </FormControl>
                 <FormControl fullWidth required>
                   <InputLabel>Prize Tier</InputLabel>
                   <Select
                     defaultValue={bulkFormData.prizeTier}
                     label="Prize Tier"
                     data-testid="bulk-prize-tier-select"
                   >
                     <MenuItem value="Prize 1">Prize 1</MenuItem>
                     <MenuItem value="Prize 2">Prize 2</MenuItem>
                     <MenuItem value="Prize 3">Prize 3</MenuItem>
                     <MenuItem value="Starters/Prize 4">Starters/Prize 4</MenuItem>
                     <MenuItem value="Consolation/Prize 5">Consolation/Prize 5</MenuItem>
                     <MenuItem value="Prize 6">Prize 6</MenuItem>
                </Select>
                 </FormControl>
                <TextField
                  label="Multiplier"
                  type="number"
                  defaultValue={bulkMultiplierInput}
                  fullWidth
                  required
                  helperText="e.g., 2.5 for 2.5x multiplier"
                  data-testid="bulk-multiplier-input"
                />
                <TextField
                  label="Effective From"
                  type="date"
                  defaultValue={bulkFormData.effectiveFrom}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  data-testid="bulk-effective-from-input"
                />
                <TextField
                  label="Effective To (Optional)"
                  type="date"
                  defaultValue={bulkFormData.effectiveTo || ''}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText="Leave empty for ongoing rule"
                  data-testid="bulk-effective-to-input"
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkCreateDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleBulkCreateRules}
              variant="contained"
              disabled={bulkCreateRulesMutation.isPending}
              color="warning"
            >
              {bulkCreateRulesMutation.isPending ? 'Creating...' : 'Create Rules for All Users'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default PayoutRulesManagement;